<script lang="ts">
  import {getContext, onDestroy, onMount} from 'svelte';
  import Swal from 'sweetalert2';

  import {FileService, VideoService, appEvents} from '@/services';
  import * as Store from '@/store';
  import {user} from '@/store';

  import FileReencodeToggle from '@/components/file-upload/FileReencodeToggle.svelte';
  import {TrimVideoOptions, VideoUploadPayload} from '@/types/upload';
  import MediaInfo from 'mediainfo.js';
  import Dialog from '../../../components/dialog/Dialog.svelte';
  import LanguagePicker from '../LanguagePicker.svelte';
  import {TrimVideoDialog, TrimVideoEvent} from './TrimVideoDialog';
  import {UploadSource} from '@/types/common';

  export let isUploadLocalDialogOpen = false;
  export let isUploading: boolean;

  let canSubmit: boolean = false;
  let isTrimVideoDialogOpen = false;
  let trimVideoOptions: TrimVideoOptions | null = null;
  let localInputErrorMessage = '';
  let localFileMetadata: {filename: string; size: number} | null = null;
  let uploadForm: HTMLFormElement;
  let file: File | null = null;
  let name: string = '';
  let fileMetadata: string | null = null;
  let selectedLanguage: string = 'English';
  // Encoder Variables
  let reencode: boolean = false;
  let currentProgress = 0;
  let title = 'Uploading Video';
  let subtitle = 'This typically takes a few minutes but can take longer for some really large videos.';

  const simpleModal: {open: Function; close: Function} = getContext('simple-modal');
  const videoService = new VideoService();
  const fileService = new FileService();

  function reset() {
    name = '';
    file = null;
    localInputErrorMessage = '';
    localFileMetadata = null;
    canSubmit = false;
    title = 'Uploading Video';
    subtitle = 'This typically takes a few minutes but can take longer for some really large videos.';
    trimVideoOptions = null;
  }

  async function wasmVideoProcessing(inputFile: File) {
    const mediaInfo = await MediaInfo();

    const getSize = () => inputFile.size;

    const metadataResult = await mediaInfo.analyzeData(
      getSize,
      (chunkSize: number, offset: number) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event: Event) => {
            const target: any = event.target;
            if (target.error) return reject(target.error);
            name = inputFile.name.split('.')[0];
            resolve(new Uint8Array(target.result));
          };
          reader.readAsArrayBuffer(inputFile.slice(offset, offset + chunkSize));
        })
    );

    const mediaTracks = metadataResult?.media?.track || [];

    if (mediaTracks.length <= 0) {
      localInputErrorMessage = 'Failed to load metadata of video';
      return;
    }

    const videoMetadataTrack = mediaTracks[1];
    const isHDR =
      videoMetadataTrack?.BitDepth === 10 || videoMetadataTrack?.Encoded_Library_Settings?.includes('hdr10');

    if (videoMetadataTrack.Format == 'HEVC') {
      reencode = true;
    }

    if (isHDR) {
      Swal.fire({
        titleText: 'HDR Video Detected!',
        html: 'Currently HDR videos are not supported on Sermon Shots. Please adjust the settings of your video recording platform and try again. If you have questions, you can <a href="/contact" class="text-blue-500">contact us here</a>.',
        icon: 'error'
      });
      return false;
    }
    return true;
  }

  async function readVideoFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const dataTransfer = (event as DragEvent).dataTransfer;

    canSubmit = false;

    const inputFile = (input?.files || dataTransfer?.files || [])[0] || null;
    if (!inputFile) {
      localInputErrorMessage = 'File is empty';
      return;
    }

    const isVideo = inputFile.type.split('/')[0] === 'video';

    if (!isVideo) {
      localInputErrorMessage = 'File selected is not a video.';
      return;
    }

    const maxSizeMB = 10240; // 10GB
    const sizeMB = parseFloat((inputFile.size / 1024 / 1024).toFixed(2));

    if (sizeMB > maxSizeMB) {
      localInputErrorMessage = 'Video File is too large.';
      return;
    }
    try {
      const isVideoValid = await wasmVideoProcessing(inputFile);
      if (!isVideoValid) return;
    } catch (err) {
      console.warn('WASM MODULE PROCESSING', err);
    }

    localInputErrorMessage = '';
    localFileMetadata = {filename: inputFile.name, size: sizeMB};

    file = inputFile;
    canSubmit = true;
  }

  async function handleUpload() {
    isUploading = true;

    const newVideo = 1;
    const maxRetries = 1;
    let retries = 0;

    const uploadChunk = async (
      url: string,
      type: string,
      dataChunk: Blob | File | FormData,
      chunkSize: number,
      chunkFirstByte: number,
      chunkLastByte: number,
      totalChunk: number
    ): Promise<{nextByes: number; error: string | null; done: boolean}> => {
      try {
        // https://cloud.google.com/storage/docs/performing-resumable-uploads#chunked-upload
        const result = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': `${type}`,
            'Content-length': `${chunkSize}`,
            'Content-range': `bytes ${chunkFirstByte}-${chunkLastByte - 1}/${totalChunk}`
          },
          body: dataChunk
        });

        if (result.status === 200) return {nextByes: 0, error: null, done: true};
        if (result.status !== 308) return {nextByes: -1, error: 'Status not 308', done: false};

        const range = result.headers.get('range');
        if (range === null) return {nextByes: -1, error: 'range not found', done: false};

        const nextByes = parseInt(range.split('-')[1]); // split "range=0-THIS_BIT"

        return {nextByes, error: null, done: false};
      } catch (error) {
        retries++;
        if (retries >= maxRetries) return {nextByes: -1, error: `Failed to upload chunk`, done: false};
        return await uploadChunk(url, type, dataChunk, chunkSize, chunkFirstByte, chunkLastByte, totalChunk);
      }
    };

    const googleCloudStorangeByteRequirement = 262144;
    const totalChunk = file!.size;
    const initialChunk = Math.round(totalChunk / 100);
    const useChunkUpload =
      totalChunk > googleCloudStorangeByteRequirement && initialChunk > googleCloudStorangeByteRequirement;

    // console.log("useChunkUpload", { useChunkUpload, totalChunk, googleCloudStorangeByteRequirement });

    let firstByteChunk = 0;
    let lastByteChunk = initialChunk;
    let chunkSize = lastByteChunk - firstByteChunk;

    const upload = async (url: string, file: File): Promise<boolean> => {
      // console.log("UPLOADING CHUNK: ", { firstByteChunk, lastByteChunk, chunkSize, totalChunk });

      const fileChunk = file.slice(firstByteChunk, lastByteChunk);
      const {nextByes, error, done} = await uploadChunk(
        url,
        file.type,
        fileChunk,
        chunkSize,
        firstByteChunk,
        lastByteChunk,
        totalChunk
      );

      Store.project.updateProgress({
        uploadProgress: Math.ceil((lastByteChunk / file.size) * 100),
        uploadLoaded: lastByteChunk,
        uploadTotal: file.size
      });

      if (error !== null) {
        Swal.fire({
          titleText: 'Problem with upload',
          text: error,
          icon: 'error'
        });
        return true;
      }

      if (!done) {
        firstByteChunk = nextByes;
        lastByteChunk = nextByes + initialChunk;

        // console.log("LAST BYTE GREATER THAN TOTAL CHUNK", (lastByteChunk >= totalChunk), { lastByteChunk, totalChunk })
        if (lastByteChunk >= totalChunk) {
          lastByteChunk = totalChunk;
          // console.log("LAST BYTE")
        }

        chunkSize = lastByteChunk - firstByteChunk;

        if (chunkSize > 0) return await upload(url, file);
      }

      return false;
    };

    const uploadSingle = async (url: string, file: File): Promise<boolean> => {
      // console.log("UPLOADING SINGLE: ", { file });

      try {
        const result = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Length': `${file.size}`
          },
          body: file
        });

        if (result.status !== 200) throw 'Status not 200';

        return false;
      } catch (error) {
        retries++;
        if (retries >= maxRetries) {
          console.error('Upload Single reached maxium retries');
          return true;
        }
        return await uploadSingle(url, file);
      }
    };

    try {
      const signedUrl = await videoService.getSignedUrl();
      let error = false;

      if (useChunkUpload) {
        error = await upload(signedUrl.url, file!);
      } else {
        error = await uploadSingle(signedUrl.url, file!);
      }

      if (error) {
        reset();
        return;
      }

      const payload: VideoUploadPayload = {
        filename: signedUrl.filename,
        name: name,
        language: selectedLanguage,
        source: UploadSource.LOCAL,
        options: {
          reencode: Boolean(trimVideoOptions) || reencode,
          ...(trimVideoOptions && {
            trim: {
              start: trimVideoOptions.start,
              end: trimVideoOptions.end
            }
          })
        }
      };

      await new VideoService().saveSignedUrl(payload).then(() => {
        reset();
      });
    } catch (error) {
      console.error(error);
      encodingError();
    }
  }

  function handleTrimVideo() {
    isUploadLocalDialogOpen = false;
    isTrimVideoDialogOpen = true;
  }

  async function handleTrimAndUpload(e: CustomEvent<TrimVideoEvent>) {
    isTrimVideoDialogOpen = false;
    if (e.detail) trimVideoOptions = {...e.detail};
    await handleUpload();
  }

  function handleFormSubmit(trim: boolean = false) {
    if (!file) {
      localInputErrorMessage = 'No file selected';
      return;
    }
    if (!name || name === '') {
      localInputErrorMessage = 'Please enter a name for the video';
      return;
    }
    trim ? handleTrimVideo() : handleUpload();
  }

  function handleFormClose() {
    isUploadLocalDialogOpen = false;
  }

  function encodingStarted() {
    title = 'Optimizing Video';
    subtitle =
      "Please note: Optimization may take up to 10 minutes. Only utilize this feature if necessary, such as when audio doesn't sync with video, which affects roughly 5% of user's videos.";
    isUploading = true;
    isUploadLocalDialogOpen = false;
  }

  async function encodingFinished(result: any) {
    isUploading = true;
    isUploadLocalDialogOpen = false;
  }

  async function encodingError() {
    isUploading = false;
    isUploadLocalDialogOpen = false;
  }

  async function toggleResync() {
    reencode = !$user?.preferences?.resync;
    const payload = {
      resync: reencode
    };

    await user.updatePreferences(payload);
  }

  const preferencesSubscription = user.subscribe(user => {
    const resync = user?.preferences?.resync;
    const language = user?.preferences?.language;
    reencode = resync || false;
    selectedLanguage = language || 'English';
  });

  Store.project.subscribe(project => {
    currentProgress = project.uploadProgress;
  });

  onMount(() => {
    appEvents.on('video-encoding-started', encodingStarted);
    appEvents.on('video-encoding-finished', encodingFinished);
    appEvents.on('video-encoding-error', encodingError);
    appEvents.on('video-encoding-progress', result => {
      name = name != '' ? name : 'New Video';
      title = 'Optimizing Video';
      isUploading = true;
      isUploadLocalDialogOpen = true;
      currentProgress = result.data.progress;
      subtitle =
        "Please note: Optimization may take up to 10 minutes. Only utilize this feature if necessary, such as when audio doesn't sync with video, which affects roughly 5% of user's videos.";
    });

    reencode = $user?.preferences?.resync || false;
    selectedLanguage = $user?.preferences?.language || 'English';
  });

  onDestroy(() => {
    preferencesSubscription();
    appEvents.off('video-encoding-started', encodingStarted);
    appEvents.off('video-encoding-finished', encodingFinished);
    appEvents.off('video-encoding-error', encodingError);
  });
</script>

{#if file && isTrimVideoDialogOpen}
  <TrimVideoDialog video={file} on:submit={handleTrimAndUpload} on:close={() => (isTrimVideoDialogOpen = false)} />
{/if}

<Dialog bind:isDialogOpen={isUploadLocalDialogOpen} useIfShow={true}>
  <div class="pointer-events-none flex h-full w-full items-end justify-center overflow-y-auto lg:items-center">
    {#if isUploading}
      <div
        class="pointer-events-auto flex h-auto w-full min-w-[280px] max-w-[680px] flex-col gap-4 rounded-2xl border border-zinc-400 bg-white p-10 shadow-lg dark:border-zinc-600 dark:bg-zinc-700"
      >
        <div class="mb-5 flex items-center justify-center text-left text-xs text-gray-300">
          <div class="loader"></div>
        </div>
        <div class="w-full text-center text-lg font-medium">
          <span class="block">{title}</span>
          {#if currentProgress != 0}
            <div class="my-4 flex flex-col items-center justify-center gap-4">
              <div class="h-2.5s relative w-full rounded-full bg-gray-600">
                <div class="absolute h-2.5 w-full animate-pulse rounded-full bg-gray-500 bg-opacity-30" />
                <div
                  class="absolute z-50 h-2.5 rounded-full bg-gradient-to-r from-primary to-secondary transition-all duration-500 ease-in-out"
                  style="width: {currentProgress}%"
                />
              </div>
              <div class="flex w-full justify-between text-sm">
                <div>Processing...</div>
                <div>{Math.round(currentProgress)}%</div>
              </div>
            </div>
          {/if}
          {#if subtitle}
            <span class="block text-xs text-gray-500">{subtitle}</span>
          {/if}
        </div>
      </div>
    {:else}
      <form
        bind:this={uploadForm}
        class="pointer-events-auto h-auto max-h-[80vh] w-full min-w-[280px] overflow-y-auto rounded-t-2xl border border-zinc-400 bg-white p-12 shadow-lg dark:border-zinc-600 dark:bg-zinc-700 lg:max-w-[680px] lg:rounded-3xl"
      >
        <div class="flex pb-2 text-black dark:text-zinc-200">
          <div class="flex items-center gap-2 py-1.5">
            <h1 class="text-lg font-bold">Upload a Video</h1>
          </div>
          <button on:click|preventDefault={handleFormClose} class="ml-auto rounded-full px-2 hover:bg-zinc-100">
            <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
          </button>
        </div>
        <!-- <hr class="border-t border-t-gray-300 dark:border-zinc-600"> -->
        <div class="grid pt-6">
          <div class="py-2">
            <label for="none" class="block text-sm font-medium text-sermon-base-300 dark:text-zinc-200">Video*</label>
            <div class="flex pt-2">
              <label
                on:drop|preventDefault={readVideoFile}
                on:dragover|preventDefault={() => null}
                for="video-upload"
                class="flex h-40 w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-300 dark:border-zinc-500 dark:bg-zinc-800"
              >
                <div class="flex flex-col items-center justify-center text-zinc-500 dark:text-zinc-400">
                  {#if !localFileMetadata}
                    <i class="fa-regular fa-cloud-arrow-up p-2 text-3xl text-gray-500"></i>
                    <div class="text-center">
                      <p class="text-sm"><strong>Click to upload</strong> or drag and drop</p>
                      <!-- <p class="text-xs">MP4, MKV, MOV, AVI, or FLV</p> -->
                    </div>
                  {:else}
                    <i class="material-symbols-rounded text-4xl">videocam</i>
                    <div class="max-w-[200px] text-center">
                      <p class="truncate text-xs">{localFileMetadata.filename}</p>
                      <p class="truncate text-xs">Size: {localFileMetadata.size}MB</p>
                    </div>
                  {/if}
                </div>
              </label>
              <input on:change={readVideoFile} class="hidden" type="file" id="video-upload" accept="video/*" />
            </div>
            <p
              class="block py-2 text-sm {localInputErrorMessage !== ''
                ? 'text-primary'
                : 'text-zinc-500 dark:text-zinc-400'}"
            >
              {localInputErrorMessage !== '' ? localInputErrorMessage : ''}
            </p>
          </div>
          <div class="py-2">
            <label for="name" class="block text-sm font-medium text-sermon-base-300 dark:text-zinc-200">Name*</label>
            <div class="flex pt-2">
              <input
                bind:value={name}
                name="name"
                type="text"
                placeholder=""
                class="block w-full rounded rounded-tl-lg border border-gray-300 px-4 py-2.5 text-sermon-base-300 placeholder-gray-300 focus:border-primary focus:outline-none focus:ring focus:ring-primary focus:ring-opacity-40 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:placeholder-zinc-500 dark:focus:border-blue-300"
                required
              />
            </div>
          </div>
          <div class="py-2">
            <LanguagePicker />
          </div>
          <div class="py-2">
            <label for="username" class="block text-sm font-medium text-sermon-base-300 dark:text-zinc-200"
              >Other Options</label
            >
            <FileReencodeToggle bind:reencode />
          </div>
        </div>
        <!-- <hr class="border-t border-t-gray-300 dark:border-zinc-600"> -->
        <div class="flex justify-end gap-2 pt-6">
          <button on:click|preventDefault={handleFormClose} class="btn btn-tertiary !text-sermon-base-300">
            <span>Cancel</span>
          </button>
          {#if file}
            <button
              type="button"
              on:click|preventDefault={() => handleFormSubmit(true)}
              class="btn btn-secondary"
              disabled={!canSubmit || isUploading}
            >
              <span class="font-medium">Trim Video</span>
            </button>
          {/if}
          <button
            type="button"
            on:click|preventDefault={() => handleFormSubmit(false)}
            class="btn btn-primary"
            disabled={!canSubmit || isUploading}
          >
            <div
              class="{isUploading
                ? ''
                : 'hidden'} spinner h-4 w-4 !border-zinc-100 !border-t-primary dark:!border-zinc-700 dark:!border-t-rose-500"
            ></div>

            <span class="font-medium">Upload</span>
          </button>
        </div>
      </form>
    {/if}
  </div>
</Dialog>

<style>
  .loader {
    margin: auto;
    border: 0.25rem solid #eaf0f6;
    border-radius: 50%;
    border-top: 0.25rem solid #ef446e;
    width: 4rem;
    height: 4rem;
    animation: spinner 1s linear infinite;
  }

  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
